import { getImageSizeFromUrl } from '@Components/BigBrowse/BBCalculationsUtils';
import type { Position2D } from '@Components/BigBrowse/Model/ModelTypes';
import {
  useDebouncedClick,
  useItemTransformAnimations,
} from '@Components/BigBrowse/bb2/meshes2/primitives/primitives.common';
import { useOnHoverMousePointer } from '@Utils/utilStyles';
import { a } from '@react-spring/three';
import { observer } from 'mobx-react-lite';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { HeroMaskGeometry } from '../../../GridCardMesh/Components/Shapes/HeroMaskGeometry';
import { useBbContentPresenter } from '../../BbContentPresenter';
import { ImageMaterial2 } from './ImageMaterial2';

type Props = {
  size: number;
  position: Position2D;
  uniqueKey: string;
  imageUrl?: string;
  onClick?: () => void;
  transparencyColor?: string;
  children?: ReactNode;
  hoverDisabled?: boolean;
  onImageLoad?: () => void;
  useCache?: boolean;
};

export const ProductCardIrregular = observer(function ProductCardIrregular({
  size,
  position,
  onClick: onClickCallback,
  imageUrl,
  transparencyColor,
  children,
  hoverDisabled = false,
  uniqueKey,
  onImageLoad,
  useCache = false,
}: Props) {
  const [onIn, onOut] = useOnHoverMousePointer(
    Boolean(onClickCallback) && !hoverDisabled
  );

  const bbContentPresenter = useBbContentPresenter();
  const {
    animating: { value: animating },
  } = bbContentPresenter;

  const [scale, setScale] = useState(1);

  const {
    x: xAnim,
    y: yAnim,
    scale: scaleAnim,
  } = useItemTransformAnimations(uniqueKey, position, scale);

  const bmpSize = useMemo(() => getImageSizeFromUrl(imageUrl), [imageUrl]);

  const { onPointerUp, onPointerDown, onClick } =
    useDebouncedClick(onClickCallback);

  const onPointerOver = () => {
    onIn();

    if (hoverDisabled) {
      return;
    }

    if (animating) {
      setScale(1);
      return;
    }

    setScale(1.1);
  };

  const onPointerOut = () => {
    onOut();
    !hoverDisabled && setScale(1);
  };

  return (
    <a.mesh
      position-x={xAnim}
      position-y={yAnim}
      position-z={0}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      frustumCulled={false}
      scale={scaleAnim}
    >
      <HeroMaskGeometry uniqueKey={uniqueKey} />
      {imageUrl ? (
        <ImageMaterial2
          imageUrl={imageUrl}
          size={size}
          bmpSize={bmpSize}
          transparencyColor={transparencyColor}
          onImageLoad={onImageLoad}
          useCache={useCache}
        />
      ) : (
        <meshBasicMaterial color='black' />
      )}
      {children}
    </a.mesh>
  );
});
